import { Box, Button } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { Form, Formik } from "formik";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import * as yup from "yup";
import useAPI from "../../../../../api/hooks/useAPI";
import { jiraKeys } from "../../../../../api/queryKeys";
import { useJiraQueries } from "../hooks/useJiraQueries";
import JiraAssignee from "./fields/JiraAssignee";
import JiraCustomFields, {
  getCustomFieldsValidationSchema,
} from "./fields/JiraCustomFields";
import JiraDescription from "./fields/JiraDescription";
import JiraIncludeEntities from "./fields/JiraIncludeEntities";
import JiraIssueType from "./fields/JiraIssueType";
import JiraLabels from "./fields/JiraLabels";
import JiraSelectedMisconfigurations from "./fields/JiraSelectedMisconfigurations";
import JiraSummary from "./fields/JiraSummary";
import { JIRA_FORM_WIDTH } from "./JiraFormSelector";

const JiraTicketForm = forwardRef(
  (
    { fullControl, selectedProject, issueAutoGeneratedFields, onSubmit },
    ref
  ) => {
    const { fetchJiraIssueCustomFields } = useAPI();
    const [selectedIssueType, setSelectedIssueType] = useState(null);
    // For extending the labels field with default config labels
    const [labelsAlreadyExtended, setLabelsAlreadyExtended] = useState(false);
    const isPerMisconfiguration =
      issueAutoGeneratedFields?.selectedMisconfigurations !== null;
    const formikRef = useRef();
    const {
      defaultIssueConfig,
      defaultIssueConfigLIsSuccess,
      issueTypes,
      issueTypesIsSuccess,
      assigneeOptions,
      assigneeOptionsIsSuccess,
      labelOptions,
    } = useJiraQueries(selectedProject);

    const formInitialValues = {
      projectName: selectedProject,
      issueType: null,
      issueSummary: issueAutoGeneratedFields?.summary || "",
      description: issueAutoGeneratedFields?.description || "",
      assignee: null,
      labels: issueAutoGeneratedFields?.labels || [],
      selectedMisconfigurations:
        issueAutoGeneratedFields?.selectedMisconfigurations,
      includeEntities: !!issueAutoGeneratedFields?.entity_ids,
    };

    // Extend the form initial values with the default issue config labels
    const extendDefaultLabels = () => {
      if (
        !labelsAlreadyExtended &&
        defaultIssueConfigLIsSuccess &&
        defaultIssueConfig.data?.labels?.length > 0 &&
        formikRef.current
      ) {
        const currentLabels = formikRef.current.values?.labels || [];
        const defaultLabels = defaultIssueConfig.data?.labels;

        formikRef.current.setFieldValue("labels", [
          ...currentLabels,
          ...defaultLabels,
        ]);

        setLabelsAlreadyExtended(true);
      }
    };

    useEffect(() => {
      extendDefaultLabels();
    }, [defaultIssueConfig, defaultIssueConfigLIsSuccess]);

    const selectCustomFields = (response) => {
      if (!response.data) {
        return [];
      }

      return Object.values(response.data)
        .filter(
          (field) =>
            field[1]?.required && field[1]?.key.startsWith("customfield_")
        )
        .map((field) => field[1]);
    };

    const { data: customFields, isLoading: isLoadingCustomFields } = useQuery({
      queryKey: jiraKeys.jiraCustomFields(
        selectedProject?.key,
        selectedIssueType?.key
      ),
      queryFn: () =>
        fetchJiraIssueCustomFields(selectedProject.key, selectedIssueType.key),
      enabled: !!selectedProject && !!selectedIssueType,
      select: selectCustomFields,
    });

    const formValidationSchema = useMemo(() => {
      const baseSchema = {
        projectName: yup
          .object()
          .nullable()
          .required("Project name is required"),
        issueType: yup.object().nullable().required("Issue type is required"),
        issueSummary: yup.string().required("Issue summary is required"),
        assignee: yup.object().nullable().required("Assignee is required"),
      };

      if (isPerMisconfiguration) {
        baseSchema.selectedMisconfigurations = yup
          .array()
          .min(1, "At least one misconfiguration must be selected")
          .required("Selected misconfigurations are required");
      }

      const customFieldsSchema = getCustomFieldsValidationSchema(customFields);

      return yup.object().shape({
        ...baseSchema,
        ...customFieldsSchema,
      });
    }, [customFields]);

    // Expose the form's submit function through the ref
    useImperativeHandle(ref, () => ({
      submitForm: () => formikRef.current?.handleSubmit(),
    }));

    return (
      <Box sx={{ width: JIRA_FORM_WIDTH }}>
        <Formik
          innerRef={formikRef}
          onSubmit={onSubmit}
          initialValues={formInitialValues}
          validationSchema={formValidationSchema}
          enableReinitialize
        >
          {({ handleSubmit }) => (
            <Form onSubmit={handleSubmit}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <JiraIssueType
                  options={issueTypes}
                  showOptions={issueTypesIsSuccess}
                  selectedOption={selectedIssueType}
                  setSelectedOption={setSelectedIssueType}
                />
                <JiraSummary />
                <JiraDescription />
                <JiraAssignee
                  options={assigneeOptions}
                  showOptions={assigneeOptionsIsSuccess}
                />
                <JiraCustomFields
                  customFields={customFields}
                  isLoading={isLoadingCustomFields}
                  assigneeOptions={assigneeOptions}
                />
                <JiraLabels labelOptions={labelOptions} />

                {isPerMisconfiguration ? (
                  <JiraSelectedMisconfigurations fullControl={fullControl} />
                ) : (
                  <JiraIncludeEntities />
                )}
              </Box>
              <Button type="submit" style={{ display: "none" }} />
            </Form>
          )}
        </Formik>
      </Box>
    );
  }
);

export default JiraTicketForm;
